import { Box, Typography, Grid, Paper, IconButton, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { 
  Assignment,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Refresh
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { PieChart } from '@mui/x-charts';
import { CasesByPeriodsResponseDto } from '@bandido/shared/dtos';

interface ComparativeItem {
  title: string;
  today: number | string;
  yesterday: number | string;
  thisWeek: number | string;
  lastWeek: number | string;
  icon: typeof Assignment;
}

const getComparativeData = (t: (key: string) => string, casesByPeriods?: any, casesResolvedByPeriods?: any): ComparativeItem[] => [
  {
    title: t('dashboard.comparatives.casesResolved'),
    today: casesResolvedByPeriods?.today || 0,
    yesterday: casesResolvedByPeriods?.yesterday || 0,
    thisWeek: casesResolvedByPeriods?.thisWeek || 0,
    lastWeek: casesResolvedByPeriods?.lastWeek || 0,
    icon: Assignment
  },
  {
    title: t('dashboard.comparatives.casesCreated'),
    today: casesByPeriods?.today || 0,
    yesterday: casesByPeriods?.yesterday || 0,
    thisWeek: casesByPeriods?.thisWeek || 0,
    lastWeek: casesByPeriods?.lastWeek || 0,
    icon: Assignment
  }
];

const getTrendIcon = (current: number | string, previous: number | string) => {
  const currentNum = typeof current === 'number' ? current : parseFloat(current.toString());
  const previousNum = typeof previous === 'number' ? previous : parseFloat(previous.toString());
  
  if (currentNum === previousNum) return { icon: TrendingFlat, color: 'text.secondary' };
  if (currentNum > previousNum) return { icon: TrendingUp, color: 'success.main' };
  return { icon: TrendingDown, color: 'error.main' };
};



interface ComparativesSectionProps {
  casesByPeriods?: CasesByPeriodsResponseDto;
  casesResolvedByPeriods?: CasesByPeriodsResponseDto;
  casesNeedAgentByTimeOpen?: {
    oneHourOrLess: number;
    oneToThreeHours: number;
    threeToEightHours: number;
    eightToTwentyFourHours: number;
    twentyFourToFortyEightHours: number;
    moreThanFortyEightHours: number;
  };
  selectedAgencies?: number[];
  startDate?: string;
  endDate?: string;
  onRefresh?: () => void;
}

export const ComparativesSection = ({ 
  casesByPeriods, 
  casesResolvedByPeriods, 
  casesNeedAgentByTimeOpen,
  selectedAgencies,
  startDate,
  endDate,
  onRefresh 
}: ComparativesSectionProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  
  const timeAgoColors = {
    '0-1': theme.palette.success.main,
    '1-3': theme.palette.success.light,
    '3-8': theme.palette.warning.main,
    '8-24': theme.palette.error.light,
    '24-48': theme.palette.error.main,
    '+48': theme.palette.error.dark,
  };
  
  const needAgentByTimeData = casesNeedAgentByTimeOpen ? [
    { timeRange: '0-1', count: casesNeedAgentByTimeOpen.oneHourOrLess },
    { timeRange: '1-3', count: casesNeedAgentByTimeOpen.oneToThreeHours },
    { timeRange: '3-8', count: casesNeedAgentByTimeOpen.threeToEightHours },
    { timeRange: '8-24', count: casesNeedAgentByTimeOpen.eightToTwentyFourHours },
    { timeRange: '24-48', count: casesNeedAgentByTimeOpen.twentyFourToFortyEightHours },
    { timeRange: '+48', count: casesNeedAgentByTimeOpen.moreThanFortyEightHours },
  ] : [];

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          {t('dashboard.sections.quickComparisons')}
        </Typography>
        {onRefresh && (
          <Tooltip title={t('common.refreshData')}>
            <IconButton
              onClick={onRefresh}
              size="small"
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      
      <Grid container spacing={3}>
        {getComparativeData(t, casesByPeriods, casesResolvedByPeriods).map((item, index) => {
          const IconComponent = item.icon;
          const dayTrend = getTrendIcon(item.today, item.yesterday);
          const weekTrend = getTrendIcon(item.thisWeek, item.lastWeek);
          const DayTrendIcon = dayTrend.icon;
          const WeekTrendIcon = weekTrend.icon;

          return (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <IconComponent color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2" fontWeight="medium">
                    {item.title}
                  </Typography>
                </Box>
                
                {/* Hoy vs Ayer */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="textSecondary">
                    {t('dashboard.comparatives.todayVsYesterday')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h6" color="primary">
                        {item.today}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t('dashboard.comparatives.vs')} {item.yesterday}
                      </Typography>
                    </Box>
                    <DayTrendIcon sx={{ color: dayTrend.color, fontSize: 24 }} />
                  </Box>
                </Box>

                {/* Esta semana vs semana pasada */}
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    {t('dashboard.comparatives.thisWeekVsPrevious')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {item.thisWeek}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t('dashboard.comparatives.vs')} {item.lastWeek}
                      </Typography>
                    </Box>
                    <WeekTrendIcon sx={{ color: weekTrend.color, fontSize: 20 }} />
                  </Box>
                </Box>
              </Paper>
            </Grid>
          );
        })}
        
        {/* Pie Chart para casos que necesitan agente por tiempo */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              {t('dashboard.comparatives.casesNeedingAgent')}
            </Typography>
            {needAgentByTimeData && needAgentByTimeData.length > 0 ? (
              <PieChart
                series={[
                  {
                    data: needAgentByTimeData.map((item) => ({
                      id: item.timeRange,
                      value: item.count,
                      label: t(`dashboard.comparatives.timeRanges.${item.timeRange}`),
                      color: timeAgoColors[item.timeRange as keyof typeof timeAgoColors],
                    })),
                    innerRadius: 40,
                    outerRadius: 80,
                    paddingAngle: 2,
                    cornerRadius: 5,
                  },
                ]}
                height={200}
              />
            ) : (
              <Box sx={{ 
                height: 200, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                color: 'text.secondary'
              }}>
                <Typography variant="body2">
                  {t('dashboard.sections.noDataAvailable')}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
}; 